import React from 'react';
import Box from '@material-ui/core/Box';
import Divider from '@material-ui/core/Divider';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import IconButton from '@material-ui/core/IconButton';
import Typography from '@material-ui/core/Typography';
import OutlinedInput from '@material-ui/core/OutlinedInput';

import { Clear, NotInterested } from '@material-ui/icons';
import { useTranslation } from 'react-i18next';

import { ToggleButton, ToggleButtonGroup } from '@material-ui/lab';
import ColorPicker from '../../../../../../../../../reusable/ColorPicker/ColorPicker';
import { IconNames, getIconByName } from '../../../../../../../../../reusable/IconLibrary/IconLibrary';
import CustomSlider from '../../../../../../../../../reusable/Slider/CustomSlider';
import { getWidgetProps } from '../../../../../../../utils/helper';
import CardData from '../../../../../../../utils/CardData';
import { useCustomStyles } from "../../../../utils"
import TabBar from '../../../../../../../utils/TabBar';
import { WidgetProps } from '../../../../../../widgetOverlay/widgetInteractionItem/WidgetProps';

const SLIDER_OPTION_MARKS = [
    { value: 0, label: '0px' },
    { value: 20 },
    { value: 40 },
    { value: 60 },
    { value: 80 },
    { value: 100 },
    { value: 120 },
    { value: 140 },
    { value: 160 },
    { value: 180 },
    { value: 200, label: '200px' },
];

type ShapeContentTypeProps = {
    widgetTemplate: WidgetProps.WidgetTemplate;
    textHoverSetting: boolean;
    setTextHoverSetting: (_value: boolean) => {};
    handleTextLabelColorChange: (_event: {} | undefined, _color: {}) => {};
    handleTextLabelHoverColorChange: (_event: {} | undefined, _color: {}) => {};
    handleUpdateIconToButton: (_iconKey: IconNames | '', _size?: string, _position?: string, _spacing?: string) => {};
    onShowIconLibraryPickerModal: () => {};

    handleBGColorChange: (_event: {} | undefined, _color: {}) => {};
    handleHoverBGColorChange: (_event: {} | undefined, _color: {}) => {};
};

const ShapeContent = ({
    widgetTemplate: widgetTemplateProp,
    textHoverSetting,
    setTextHoverSetting,
    handleTextLabelColorChange,
    handleTextLabelHoverColorChange,
    handleUpdateIconToButton,
    onShowIconLibraryPickerModal,
}: ShapeContentTypeProps) => {
    const widgetTemplate = getWidgetProps(widgetTemplateProp) as WidgetProps.WidgetTemplate;
    const {
        style: {
            textLabel: { color, hoverColor } = {},
            iconConfig: { size, position, spacing } = {}
        },
        settings: { iconKey } = {},
    } = widgetTemplate;

    const { t: translator } = useTranslation();
    const classes = useCustomStyles()

    const isShowIconOpt = Boolean(iconKey);

    const IconToShowOnToggle = getIconByName((iconKey || 'AddCircleOutline') as IconNames, {
        color: isShowIconOpt ? 'primary' : 'none',
    });

    const IconToShowOnPicker = getIconByName((iconKey || 'AddCircleOutline') as IconNames, {});

    return (
        <CardData title={translator('SCENARIO_SCENE_EDITOR_WIDGET_SETTING_CONTENT')}>
            <div className="widget-props-wrapper">
                <div className="scene-widget__control">
                    <TabBar
                        size="big"
                        items={[
                            {
                                label: translator('SCENARIO_SCENE_EDITOR_WIDGET_SETTING_CONTENT_SETTING_NORMAL'),
                                id: 'normal',
                                selected: !textHoverSetting,
                            },
                            {
                                label: translator('SCENARIO_SCENE_EDITOR_WIDGET_SETTING_CONTENT_SETTING_HOVER'),
                                id: 'hover',
                                selected: textHoverSetting,
                            },
                        ]}
                        onItemClick={(_event: any, item: any) =>  setTextHoverSetting(item.id === 'hover')}
                    />
                </div>
                {!textHoverSetting && (
                    <FormControlLabel
                        className={'scenario-chart__control'}
                        label={translator('SCENARIO_SCENE_EDITOR_WIDGET_SETTING_CONTENT_SETTING_BUTTON_ICON_COLOR')}
                        onChange={handleTextLabelColorChange}
                        labelPlacement="start"
                        control={
                            <div className="scene-widgets__sort-input-control">
                                <OutlinedInput value={color} className={classes.input} />
                                <ColorPicker
                                    width="28px"
                                    height="28px"
                                    padding="4px"
                                    color={color}
                                    onChangeComplete={(color: any) => handleTextLabelColorChange(undefined, color)}
                                />
                                <IconButton
                                    size="small"
                                    onClick={() => handleTextLabelColorChange(undefined, { hex: '' })}
                                >
                                    <Clear />
                                </IconButton>
                            </div>
                        }
                    />
                )}
                {textHoverSetting && (
                    <FormControlLabel
                        className={'scenario-chart__control'}
                        label={translator('SCENARIO_SCENE_EDITOR_WIDGET_SETTING_CONTENT_SETTING_BUTTON_ICON_COLOR')}
                        onChange={handleTextLabelHoverColorChange}
                        labelPlacement="start"
                        control={
                            <div className="scene-widgets__sort-input-control">
                                <OutlinedInput value={hoverColor ?? color} className={classes.input} />
                                <ColorPicker
                                    width="28px"
                                    height="28px"
                                    padding="4px"
                                    color={hoverColor ?? color}
                                    onChangeComplete={(color: any) => handleTextLabelHoverColorChange(undefined, color)}
                                />
                                <IconButton
                                    size="small"
                                    onClick={() => handleTextLabelHoverColorChange(undefined, { hex: '' })}
                                >
                                    <Clear />
                                </IconButton>
                            </div>
                        }
                    />
                )}
                <Divider className="divider-extra-space" />
                <Box className="button-icon-configure-container">
                    <Typography>
                        {translator('SCENARIO_SCENE_EDITOR_WIDGET_SETTING_CONTENT_SETTING_BUTTON_ICON_SETTING')}
                    </Typography>
                    <ToggleButtonGroup>
                        <ToggleButton
                            value={'NoIcon'}
                            aria-label="left aligned"
                            size="small"
                            className="toggle-button-custom"
                            onClick={() => {
                                handleUpdateIconToButton('', undefined, undefined, undefined);
                            }}
                            selected={!isShowIconOpt}
                        >
                            <NotInterested color={!isShowIconOpt ? 'primary' : 'disabled'} />
                        </ToggleButton>
                        <ToggleButton
                            value={'WithIcon'}
                            aria-label="right aligned"
                            size="small"
                            className="toggle-button-custom"
                            onClick={() => {
                                handleUpdateIconToButton(
                                    (iconKey || 'AddCircleOutline') as IconNames,
                                    size,
                                    position,
                                    spacing
                                );
                            }}
                            selected={isShowIconOpt}
                        >
                            {IconToShowOnToggle}
                        </ToggleButton>
                    </ToggleButtonGroup>
                </Box>
                {isShowIconOpt && (
                    <>
                        <div className={'scene-widget__control'}>
                            <div className="scene-widget__iconSelector--placeholder" onClick={onShowIconLibraryPickerModal}>
                                {IconToShowOnPicker}
                            </div>
                        </div>
                        <FormControlLabel
                            className={'scenario-chart__control'}
                            label={translator('SCENARIO_SCENE_EDITOR_WIDGET_SETTING_CONTENT_SETTING_ICON_SIZE')}
                            labelPlacement="start"
                            control={
                                <CustomSlider
                                    value={Number(size)}
                                    min={0}
                                    max={200}
                                    marks={SLIDER_OPTION_MARKS}
                                    onChange={(e: any, value: number | number[]) => {
                                        handleUpdateIconToButton(
                                            (iconKey || 'AddCircleOutline') as IconNames,
                                            String(value),
                                            position,
                                            spacing
                                        );
                                    }}
                                />
                            }
                        />
                    </>
                )}
            </div>
        </CardData>
    );
};

export default ShapeContent;
